/**
 * How a progression grid behaves under the keyboard and the mouse.
 *
 * One tab stop, arrows moving the focused cell, Enter opening a field, a comma parsed as a decimal
 * point, Escape contained so it cancels the cell rather than the surface, and focus landing back on
 * the cell the field belonged to.
 *
 * ── The roving tab stop itself moved to `shared/lib/useGridFocus` ────────────────────────────
 * 17 September 2026, on its second real consumer: `/raporty`'s client × survey grid is the same
 * rectangle of controls with the same 120-tab-stop problem. What stays here is what is specific to a
 * PROGRESSION cell — the keys a cell answers, and the field it opens.
 *
 * ── It is ONE grid's behaviour today, and it stays a module anyway ───────────────────────────
 * It was written for two: `DrawerMatrix` and `ProgressionMatrix` drew completely different markup —
 * a CSS grid of spans against a real `<table>` — over verbatim copies of this behaviour, down to
 * the `1e6` rounding, and a parse fix or a focus fix landed in one and not the other. Both are gone
 * with the 14 September port and `SheetMatrix` is the only caller left (`TypeMenu` takes the cell
 * keys alone, for the row header it owns). What survives the deletion test is the SPLIT: this is
 * the interaction contract, the component renders nothing from here except the field, and the
 * arithmetic and the focus rules are testable without a grid around them.
 *
 * It sits in `lib/` rather than `ui/` because it is not a surface.
 */

import { useRef, useState, type KeyboardEvent } from 'react';

export interface MatrixCellKeys {
  readonly isEditable: boolean;
  readonly isEditing: boolean;
  /** Whether Delete and Backspace have a pin to hand back. */
  readonly isOverridden: boolean;
  readonly onEdit: () => void;
  readonly onRevert: () => void;
  /** Enter on a cell that takes no editor. The caller says the cell's own reason out loud. */
  readonly onRefused: () => void;
}

/**
 * What a cell does with a key.
 *
 * ── The guard is not defensive, it is the fix for two real defects ───────────────────────────
 * The revert ✕ lives INSIDE the cell, so its own key events bubble here. Without the target check,
 * Enter on the ✕ opened the cell's editor instead of reverting, and an arrow pressed while the ✕
 * held focus moved the grid's focus out from under it.
 *
 * ── Delete reverts, because the ✕ is no longer a tab stop ────────────────────────────────────
 * A grid whose every pinned cell adds a tab stop is not a one-tab-stop grid: a column of pins on a
 * 52-cycle phase is 52 extra stops. So the ✕ stays for the mouse at `tabIndex={-1}`, and the
 * keyboard reverts with Delete or Backspace on the cell itself — which is where the trainer's focus
 * already is, and which the ✕'s own accessible name names.
 */
export function matrixCellKeyDown(event: KeyboardEvent<HTMLElement>, keys: MatrixCellKeys): void {
  if (event.target !== event.currentTarget) return;
  if (keys.isEditing) return;

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    // A refused cell ANSWERS. Doing nothing at all reads as a broken grid rather than as a rule,
    // and the reason was reachable only by seeing which cells are pale.
    if (keys.isEditable) keys.onEdit();
    else keys.onRefused();
    return;
  }
  if (!keys.isEditable) return;
  if (keys.isOverridden && (event.key === 'Delete' || event.key === 'Backspace')) {
    event.preventDefault();
    keys.onRevert();
  }
}

interface MatrixCellEditorProps {
  /** The stored value, or `null` on an empty cell. Multiplied by `scale` to reach the typed one. */
  readonly valueRaw: number | null;
  /**
   * Typed value → stored value: divide by this. 100 on a ladder row and on the %1RM axis, 1
   * elsewhere. It rides on the CELL rather than being derived from an axis a caller chose, because
   * the one thing a pin must never take from a caller is its axis (see `MatrixCellAddress`).
   */
  readonly scale: number;
  readonly className: string;
  readonly ariaLabel: string;
  readonly onCommit: (raw: number | null) => void;
  readonly onCancel: () => void;
}

/**
 * The cell's field.
 *
 * An empty field commits `null`, which is a revert: the cell goes back to the rule. That is the same
 * meaning the ladder's own commit gives a blank, so there is one keystroke for "undo this pin".
 *
 * ── It opens SELECTED ──────────────────────────────────────────────────────────────────────
 * `autoFocus` alone put the caret after the number already in the field, so Enter, `10`, Enter on a
 * cell reading 80 committed 810 — a value nobody typed, on a surface whose whole job is to show the
 * trainer what they typed. Selecting the text makes the first keystroke a replacement, which is what
 * a one-value field is for.
 *
 * It does NOT clamp, and that is deliberate rather than an omission: both writers a cell can reach
 * already do. `setMatrixOverride` runs every typed value through `normaliseTypedValue`, which is
 * `clampMeasurementValue` on a measurement column and the axis's own bounds on the %1RM fraction,
 * and a ladder row's percent is bounded inside `commitBackoffPercent`. A second clamp here would be
 * a second answer to what a trainer may type, on the one surface where the two must agree.
 *
 * Both exits — commit and cancel — hand focus back through the caller's own `onCommit`/`onCancel`,
 * which is what keeps the grid navigable afterwards.
 */
export function MatrixCellEditor({
  valueRaw,
  scale,
  className,
  ariaLabel,
  onCommit,
  onCancel,
}: MatrixCellEditorProps) {
  const [text, setText] = useState(valueRaw == null ? '' : String(valueRaw * scale));
  // A blur fired BY the commit must not commit a second time: Enter commits, React unmounts the
  // input, and the unmount blurs it.
  const committed = useRef(false);

  const commit = () => {
    if (committed.current) return;
    committed.current = true;
    const trimmed = text.trim().replace(',', '.');
    if (trimmed === '') {
      // An empty field on a cell that was already empty is not a revert of anything.
      if (valueRaw == null) onCancel();
      else onCommit(null);
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed)) {
      onCancel();
      return;
    }
    const raw = Math.round((parsed / scale) * 1e6) / 1e6;
    // A FIELD THAT COMMITS THE NUMBER IT OPENED ON CHANGED NOTHING, and a cell opened and clicked
    // away from must not be written to. The field commits on BLUR, so every glance at a cell is a
    // commit of the value already showing; `setMatrixOverride`'s own guard catches the case where
    // that value is what the STORED rule computes, and cannot catch the one where the grid is
    // previewing a staged rule the draft does not hold yet — it would pin the preview. This is the
    // same decision one layer earlier, where the question is simply whether the trainer typed
    // anything.
    if (valueRaw != null && Math.abs(raw - valueRaw) < 1e-6) {
      onCancel();
      return;
    }
    onCommit(raw);
  };

  const cancel = () => {
    if (committed.current) return;
    committed.current = true;
    onCancel();
  };

  return (
    <input
      className={className}
      type="text"
      inputMode="decimal"
      autoFocus
      value={text}
      aria-label={ariaLabel}
      onFocus={(event) => {
        event.target.select();
      }}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
        if (event.key === 'Escape') {
          // Contained: the drawer closes on Escape and the dock closes on Escape, and cancelling a
          // cell must not close either.
          event.stopPropagation();
          cancel();
        }
      }}
    />
  );
}
